import React, { createContext, useContext, useMemo } from "react";
import { useAccount } from "./AccountContext";
import { StripeSubscriptionStatus } from "@visitproof/types";

interface SubscriptionState {
  /** Whether the account has an active or trialing subscription */
  hasActiveSubscription: boolean;
  /** Whether the account is currently in a trial period */
  isInTrial: boolean;
  /** Whether the trial has expired (no active subscription) */
  isTrialExpired: boolean;
  /** Current subscription status */
  status: StripeSubscriptionStatus | undefined;
  /** Trial end date (if in trial) */
  trialEndDate: Date | null;
  /** Days remaining in trial (0 if not in trial or expired) */
  trialDaysRemaining: number;
  /** Whether to show the subscription required gate */
  shouldBlockAccess: boolean;
  /** Loading state */
  loading: boolean;
}

const SubscriptionContext = createContext<SubscriptionState | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { account, loading } = useAccount();

  const subscriptionState = useMemo((): SubscriptionState => {
    if (loading || !account) {
      return {
        hasActiveSubscription: false,
        isInTrial: false,
        isTrialExpired: false,
        status: undefined,
        trialEndDate: null,
        trialDaysRemaining: 0,
        shouldBlockAccess: false, // Don't block while loading
        loading: true,
      };
    }

    const status = account.stripeStatus as StripeSubscriptionStatus | undefined;
    const trialEnd = account.stripeTrialEnd;
    const now = Date.now();

    // Calculate trial end date
    const trialEndDate = trialEnd ? new Date(trialEnd * 1000) : null;

    // Calculate days remaining in trial
    let trialDaysRemaining = 0;
    if (trialEndDate && trialEndDate.getTime() > now) {
      trialDaysRemaining = Math.ceil((trialEndDate.getTime() - now) / (1000 * 60 * 60 * 24));
    }

    // Determine subscription states
    const isInTrial = status === 'trialing' && trialDaysRemaining > 0;
    const hasActiveSubscription = status === 'active' || status === 'trialing';

    // Trial is expired if:
    // 1. There was a trial end date that's in the past, AND
    // 2. The subscription is not active
    const isTrialExpired = Boolean(
      trialEndDate &&
      trialEndDate.getTime() < now &&
      status !== 'active'
    );

    // Block access if:
    // 1. No subscription at all (new user who hasn't started trial)
    // 2. Trial has expired
    // 3. Subscription is canceled, unpaid, or past_due
    const shouldBlockAccess =
      !status || // No subscription
      status === 'canceled' ||
      status === 'unpaid' ||
      status === 'incomplete_expired' ||
      (status === 'past_due' && !isInTrial) || // Allow past_due during trial
      isTrialExpired;

    return {
      hasActiveSubscription,
      isInTrial,
      isTrialExpired,
      status,
      trialEndDate,
      trialDaysRemaining,
      shouldBlockAccess,
      loading: false,
    };
  }, [account, loading]);

  return (
    <SubscriptionContext.Provider value={subscriptionState}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionState => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};

export const useSubscriptionOptional = (): SubscriptionState | null => {
  return useContext(SubscriptionContext) ?? null;
};
